import React from 'react';
import { Form, Input, DatePicker, Typography, Space, Radio, Button } from 'antd';
import { SaveOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import MiniPrinter from './MiniPrinter';

const { Text, Title } = Typography;

const AgreementComponent = ({ 
  form, 
  currentStatus, 
  agreementType, 
  title, 
  description, 
  defaultStructure,
  showTitle = true,
  organisation = null,
  onSaveChanges = null,
  loading = false
}) => {
  const { t } = useTranslation();
  const [currentFormStatus, setCurrentFormStatus] = useState(null);
  const [printData, setPrintData] = useState(null);
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Check if agreement is signed (either from organisation or form)
  const isSignedStatus = organisation ? 
    (agreementType === 'dsa' ? organisation.dsaStatus === 'Signed' :
     agreementType === 'dpa' ? organisation.dpaStatus === 'Signed' :
     agreementType === 'toc' ? organisation.tocStatus === 'Signed' : false) : false;

  // Watch for form changes to update button visibility
  useEffect(() => {
    const formValues = form.getFieldsValue();
    setCurrentFormStatus(formValues[`${agreementType}Status`]);
  }, [form, agreementType]);

  // Prepare print data when component mounts or userInfo changes
  useEffect(() => {
    if (userInfo) {
      preparePrintData();
    }
  }, [userInfo, agreementType]);

  // Handle status change
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    form.setFieldValue(`${agreementType}Status`, newStatus);
    setCurrentFormStatus(newStatus);
  };

  // Prepare PDF data function
  const preparePrintData = () => {
    if (!userInfo) {
      message.error(t('User information is required'));
      return;
    }

    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const generatedDate = new Date().toLocaleDateString();
      
      // Only pass the essential dynamic variables
      let templateData = {
        businessOrganisationIdentifier: userInfo.businessOrganisationIdentifier,
        effectiveDate: currentDate,
        generatedDate: generatedDate
      };
      
      // Add signature data based on agreement type
      if (agreementType === 'dsa') {
        templateData.controllerSignedBy = userInfo.dsaSignedBy || 'Pending';
        templateData.controllerSignatureDate = userInfo.dsaSignedDate || currentDate;
      } else if (agreementType === 'dpa') {
        templateData.controllerSignedBy = userInfo.dpaSignedBy || 'Pending';
        templateData.controllerSignatureDate = userInfo.dpaSignedDate || currentDate;
      } else if (agreementType === 'toc') {
        templateData.controllerSignedBy = userInfo.tocSignedBy || 'Pending';
        templateData.controllerSignatureDate = userInfo.tocSignedDate || currentDate;
      }

      setPrintData(templateData);
    } catch (error) {
      console.error('Error preparing print data:', error);
      message.error(t('Failed to prepare agreement data'));
    }
  };



  return (
    <div style={{ marginBottom: 24 }}>
      {showTitle && (
        <>
          <Title level={2} style={{ fontSize: '30px', marginBottom: 8 }}>
            {t(title)}
          </Title>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
            {t(description)}
          </Text>
          {userInfo && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {userInfo.businessOrganisationIdentifier} • OpenEP|NET Platform
              </Text>
            </div>
          )}
        </>
      )}
      
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Status Selection */}
        <div>
          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
            {t('Status')}
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Form.Item name={`${agreementType}Status`} style={{ marginBottom: 0, flex: 1 }}>
              <Radio.Group onChange={handleStatusChange} disabled={isSignedStatus}>
                <Space>
                  <Radio value="Pending">{t('Pending')}</Radio>
                  <Radio value="InProgress">{t('In Progress')}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {(currentFormStatus === 'Pending' || currentFormStatus === 'InProgress') && onSaveChanges && !isSignedStatus && (
              <Button
                size="small"
                icon={<SaveOutlined />}
                onClick={onSaveChanges}
                loading={loading}
              >
                {t('Save Changes')}
              </Button>
            )}
          </div>
        </div>

        {/* Agreement Terms - Inline PDF Rendering */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text strong style={{ fontSize: '13px' }}>
                    {t('Agreement Terms')}
                  </Text>
                </div>
          
          {printData && (
            <MiniPrinter
              configEndpoint={`/print/configs/${agreementType}_agreement`}
              generateEndpoint="/print/documents/generate"
              data={printData}
            />
          )}
                </div>

        {/* Signing Information */}
        <div>
          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
            {t('Signing Information')}
          </Text>
          <Space size={12} style={{ width: '100%' }}>
            <Form.Item name={`${agreementType}SignedDate`} label={t('Signed Date')} style={{ marginBottom: 0, flex: 1 }}>
              <DatePicker style={{ width: '100%' }} disabled={isSignedStatus} />
            </Form.Item>
            <Form.Item 
              name={`${agreementType}SignedBy`} 
              label={t('Signed By')} 
              style={{ marginBottom: 0, flex: 1 }}
              rules={[
                {
                  required: currentStatus === 'Signed',
                  message: t('Signed By is required when status is Signed')
                }
              ]}
            >
              <Input placeholder={t('Enter signer name')} disabled={isSignedStatus} />
            </Form.Item>
          </Space>
        </div>
      </Space>

    </div>
  );
};

export default AgreementComponent;
